import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Image,
  Skeleton,
  useToast,
  Heading,
  SimpleGrid,
  Flex,
  Circle,
  Text,
  Stack,
  Spinner,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchServicesSuccess,
  fetchServicesFailure,
  setSelectedServiceId,
  setCurrentStep,
} from "../reducers/LocationSlice";
import { fetchCollaboratorDetailsApi, fetchServicesApi } from "../api/api";
import ServiceCard from "./ServiceCard";
import MainBanner from "./MainBanner";
import ServiceBanner from "./ServiceBanner";
import WhyThisService from "./WhyThisService";
import DateSelector from "./DateSelector";
import BookingForm from "./BookingForm";
import Payment from "./Payment";
import WebsiteMaintainance from "../assets/images/website-maintainance.png";
import { setService, setServiceId } from "../reducers/KiidoBookingSlice";
import i18next from 'i18next';

const Content = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const toast = useToast();
  const { services, loading, apiError, selectedServiceId, currentStep } =
    useSelector((state) => state.location);

  const [loader, setLoader] = useState(false)
  const [errorColabFetch, setErrorColabFetch] = useState(false)

  const params = new URLSearchParams(location.search);
  const specialist = params.get("specialist");
  const locale = params.get("locale");

  // Add useEffect for locale handling
  useEffect(() => {
    const supportedLocales = ['en', 'fr'];
    const newLocale = locale && supportedLocales.includes(locale.toLowerCase()) 
      ? locale.toLowerCase() 
      : 'en';
    
    i18next.changeLanguage(newLocale);
  }, [locale]);

  // Define steps with translations
  const steps = [
    t("steps.select_service"), // "Select Service" / "Sélectionner un service"
    t("steps.service_theme"), // "Why this Service" / "Pourquoi ce service"
    t("steps.select_date_time"), // "Choose Date & Time" / "Choisir la date et l'heure"
    t("steps.booking_details"), // "Booking Form" / "Formulaire de réservation"
    t("steps.payment"), // "Payment" / "Paiement"
  ];
  // Step indicator component
  const StepIndicator = ({ currentStep, onStepChange }) => (
    <Flex
      direction="row"
      alignItems="flex-start" // Align steps from the top
      justifyContent="center"
      width="100%"
      marginY={2}
      paddingX={2}
      gap={2}
      display={{ base: "flex", md: "none" }} // Show only on mobile
    >
      {steps.map((step, index) => (
        <Stack
          key={index}
          direction="column"
          align="center"
          spacing={2}
          flex={1}
          cursor={index <= currentStep ? "pointer" : "default"}
          onClick={() => index <= currentStep && onStepChange(index + 1)}
        >
          <Circle
            size="28px"
            bg={index < currentStep ? "#FF6666" : "gray.300"}
            color="white"
            fontSize="sm"
          >
            {index + 1}
          </Circle>
          <Text fontSize="xs" textAlign="center" maxWidth="80px">
            {step}
          </Text>
        </Stack>
      ))}
    </Flex>
  );

  const [servicePagination, setServicePagination] = useState({
    next: null,
    previous: null,
    count: 0,
  });

  const handleOnlineServicesReq = async () => {
    setLoader(true)
    const params = new URLSearchParams(location.search);
    const specialist = params.get("specialist");
    const specialistLowCase = specialist ? specialist.toLowerCase() : '';

    // Define a helper function to handle fetching and dispatching services
    const fetchAndDispatchServices = async (apiCall) => {
      try {
        const data = await apiCall();
        // console.log("Fetched data:", data);
        dispatch(fetchServicesSuccess(data.results || data.consultation_specialities));
        setServicePagination({
          next: data.next,
          previous: data.previous,
          count: data.count,
        });
      } catch (error) {
        if (error.status) {
          setErrorColabFetch(true)
          dispatch(fetchServicesFailure(error.message ? error.message : error.statusText));
          // toast({
          //   title: "Collabrators Not Found",
          //   status: "error",
          //   duration: 5000,
          //   isClosable: true,
          // });
        } else {
          dispatch(fetchServicesFailure(error.message ? error.message : error.statusText));
          toast({
            title: t("content.down_service"),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        console.log("error.message", error.message ? error.message : error.statusText)
      } finally {
        setLoader(false)
      }
    };

    // Use the helper function based on the presence of the specialist parameter
    if (specialist) {
      await fetchAndDispatchServices(() => fetchCollaboratorDetailsApi(specialistLowCase));
    } else {
      await fetchAndDispatchServices(fetchServicesApi);
    }
  };


  useEffect(() => {
    handleOnlineServicesReq();
  }, [dispatch]);

  useEffect(() => {
    // const params = new URLSearchParams(location.search);
    // const specialist = params.get("specialist");
    const serviceId = params.get("services");

    // if (specialist) {
    //   fetchCollaborator(specialist); // Pass specialist as a string
    // }

    // Log the 'specialist' to the console
    // console.log('Specialist:', specialist);
    if (serviceId) {
      const selectedService = services.find(
        (service) => service.id.toString() === serviceId
      );
      if (selectedService) {
        dispatch(setService(selectedService));
        dispatch(setServiceId(selectedService.id));
        dispatch(setSelectedServiceId(selectedService.id));
        dispatch(setCurrentStep(2));
      }
    }
  }, [location.search, services, dispatch]);


  const handleStepChange = (step) => {
    dispatch(setCurrentStep(step)); // Handle step change correctly
  };

  const handleSelectService = (service) => {
    dispatch(setService(service));
    dispatch(setServiceId(service.id));
    dispatch(setSelectedServiceId(service.id));
    dispatch(setCurrentStep(2));

    const params = new URLSearchParams(window.location.search);
    params.set("services", service.id);
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params}`
    );
  };

  // Update fetchCollaborator function to accept 'specialist' as a string directly
  const fetchCollaborator = async (specialist) => {
    const specialistLowCase = specialist ? specialist.toLowerCase() : '';
    try {
      // Call the fetchCollaboratorDetailsApi function with the lowercase 'specialist'
      const data = await fetchCollaboratorDetailsApi(specialistLowCase);
      // console.log("Collaborator data:", data);
      // dispatch(fetchServicesSuccess(data.consultation_specialities));
      // setCollaborator(data);  // Assuming setCollaborator updates the state with the fetched data
    } catch (error) {
      console.error('Error fetching collaborator details:', error);
    }
  };


  // return <button onClick={() => {throw new Error("This is your first error!");}}>Break the world</button>;

  return (
    <div>
      {apiError && (
        <Alert status="error" marginY={4}>
          <AlertIcon />
          <AlertDescription fontSize="lg">{apiError}</AlertDescription>
          <Image src={WebsiteMaintainance} alt="Website Maintenance" />
        </Alert>
      )}

      {loading ? (
        <Box padding={4}>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} height="300px" marginBottom={4} />
          ))}
        </Box>
      ) : (
        <>
          {/* Step Indicator */}
          <StepIndicator
            currentStep={currentStep}
            onStepChange={handleStepChange}
          />

          {/* Render appropriate banner */}
          {currentStep === 1 ? <MainBanner /> : <ServiceBanner />}

          {/* Service Selection Grid */}
          {currentStep === 1 && (
            <>
              {loader ? <Box textAlign="center" py="6"><Spinner size="lg" color="#FF6666" borderWidth="3px" /></Box> :
                <>
                  {errorColabFetch ?
                    <Box textAlign="center" py="6">
                      <Text fontSize="lg" color="gray.500">{t('specialist_not_found')}</Text>
                    </Box>
                    :
                    <SimpleGrid
                      columns={{ base: 1, md: 2, lg: 3 }}
                      spacing={8}
                      padding={4}
                    >
                      {services.map((service) => (
                        <ServiceCard
                          key={service.id}
                          service={service}
                          isSelected={service.id === selectedServiceId}
                          handleSelect={() => handleSelectService(service)}
                        />
                      ))}

                    </SimpleGrid>
                  }
                </>
              }
            </>
          )}

          {/* Render additional steps */}
          {currentStep === 2 && <WhyThisService />}

          {currentStep === 3 && (
            <Box p={6} bg="white" borderRadius="md" boxShadow="md">
              <Heading as="h3" size="lg" mb={4} color="#FF6666">
                {t("content.preferred_date_time")}
              </Heading>
              <DateSelector />
              <Box mt={4} display="flex" justifyContent="space-between">
                <Button
                  onClick={() => dispatch(setCurrentStep(currentStep - 1))}
                  bg="transparent"
                  color="#FF6666"
                  border="2px solid #FF6666"
                  _hover={{ bg: "#FF6666", color: "white" }}
                >
                  {t("content.pagination.previous")}
                </Button>
                <Button
                  onClick={() => dispatch(setCurrentStep(currentStep + 1))}
                  bg="#FF6666"
                  color="white"
                  _hover={{ bg: "#E55353" }}
                >
                  {t("content.pagination.next")}
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === 4 && <BookingForm />}
          {currentStep === 5 && <Payment />}
        </>
      )}
    </div>
  );
};

export default Content;
